import { useState, useEffect, useCallback } from 'react'
import { error } from '../../../../../utils/logger'
import {
  runPipelineBatch,
  getSkipStages,
  refreshStoredConfig,
  loadGovernanceConfig,
  clearShadowFindings,
  buildShadowImpactReport,
  REVIEW_COACH_SEVERITY_THRESHOLD,
  type PipelineBatchResult,
  type ShadowImpactReport,
} from '../../../../../governance'
import { ALL_QUESTIONS_CORE } from '../../../../../content'

/**
 * Inline gate-threshold defaults — mirror DEFAULT_GATE_THRESHOLDS in
 * issueContract.ts. The review-coach threshold is sourced from the governance
 * SSOT (detangle §10.5) so this admin mirror can never drift from the gate;
 * the other three remain inline (production/assessment min-scores + per-family
 * minimum are gate-only knobs without a separate display consumer).
 */
const GATE_DEFAULTS = {
  productionMinScore: 70,
  assessmentMinScore: 82,
  reviewCoachSeverityThreshold: REVIEW_COACH_SEVERITY_THRESHOLD,
  assessmentMinQuestionsPerFamily: 3,
} as const

export interface PipelineRunState {
  isRunning: boolean
  lastRun: string | null
  runDuration: string | null
  batchResult: PipelineBatchResult | null
  /**
   * FG4 §Fase B — Shadow-mode impact report computed alongside each pipeline
   * run. Non-null after the first run completes; counts every "would-block"
   * finding emitted by modules/types configured as enforcement mode 'shadow'.
   * `null` until the first run finishes (or when enforcement is fully 'off'
   * and no module ramps to shadow yet).
   */
  shadowImpactReport: ShadowImpactReport | null
}

export interface PipelineRunController extends PipelineRunState {
  revalidate: () => void
}

/**
 * usePipelineRun — owns the pipeline execution lifecycle.
 *
 * Single responsibility:
 *  - Read stored governance config (skip-stages, pedagogy toggle, quality gates,
 *    FG4 enforcement modes + grace ledger)
 *  - Clear the shadow findings store BEFORE each run so the impact report is
 *    run-scoped (not a cumulative log)
 *  - Run `runPipelineBatch` over ALL_QUESTIONS_CORE with `enforcementConfig`
 *  - Compute `buildShadowImpactReport()` AFTER the run and surface it alongside
 *    the batch result
 *  - Track timing (last run timestamp, duration) for the admin header
 *
 * Auto-runs on mount. Exposes `revalidate` for the manual "Opnieuw valideren"
 * button.
 *
 * Extracted from useGovernanceData (R1) so the run lifecycle is independent
 * of the derivation/view-model logic. Useful for future use cases like
 * comparing two runs side-by-side (snapshot-infra route 1).
 */
export function usePipelineRun(): PipelineRunController {
  const [isRunning, setIsRunning] = useState(true)
  const [lastRun, setLastRun] = useState<string | null>(null)
  const [runDuration, setRunDuration] = useState<string | null>(null)
  const [batchResult, setBatchResult] = useState<PipelineBatchResult | null>(
    null,
  )
  const [shadowImpactReport, setShadowImpactReport] =
    useState<ShadowImpactReport | null>(null)

  const revalidate = useCallback(() => {
    setIsRunning(true)
    const startTime = Date.now()
    const now = new Date()
    const timeStr = `${now.getHours().toString().padStart(2, '0')}:${now
      .getMinutes()
      .toString()
      .padStart(2, '0')}`

    // setTimeout lets React paint the loading state before the heavy run
    setTimeout(async () => {
      try {
        await refreshStoredConfig()
        const skipStages = await getSkipStages()
        const govConfig = await loadGovernanceConfig()
        const skipPedagogy = govConfig.pipelineStages?.pedagogyEnabled === false

        const thresholds = govConfig.qualityGates
          ? {
              productionMinScore:
                govConfig.qualityGates.minQualityScore ??
                GATE_DEFAULTS.productionMinScore,
              assessmentMinScore: Math.max(
                (govConfig.qualityGates.minQualityScore ?? 70) + 12,
                GATE_DEFAULTS.assessmentMinScore,
              ),
              reviewCoachSeverityThreshold:
                GATE_DEFAULTS.reviewCoachSeverityThreshold,
              assessmentMinQuestionsPerFamily:
                GATE_DEFAULTS.assessmentMinQuestionsPerFamily,
            }
          : undefined

        // FG4 §Fase B — clear last run's shadow findings so the impact report
        // reflects ONLY this run. Without this the store accumulates across
        // runs and the "would block X questions" number drifts.
        clearShadowFindings()

        const result = runPipelineBatch(ALL_QUESTIONS_CORE, {
          ...(skipStages.length > 0 ? { skipStages } : {}),
          skipPedagogy,
          ...(thresholds ? { thresholds } : {}),
          // FG4 §Fase B — opt-in shadow/warn/enforce ramp. Strictly additive:
          // when `enforcement.pedagogical` and `enforcement.schemas` are empty
          // objects (the safe default), every issue resolves to 'off' (legacy
          // permissive) for unconfigured scopes — no behavioural change until
          // an operator flips a module to 'shadow' in GovernanceSettingsTab.
          enforcementConfig: govConfig,
        })

        // FG4 §Fase B — snapshot the shadow findings into a report. Computed
        // from the module-level store that recordShadowFinding writes to.
        const shadowReport = buildShadowImpactReport()

        const duration = ((Date.now() - startTime) / 1000).toFixed(1)
        setBatchResult(result)
        setShadowImpactReport(shadowReport)
        setLastRun(`${timeStr} (${duration}s)`)
        setRunDuration(`${duration}s`)
      } catch (err) {
        error('Validation failed:', err)
      } finally {
        setIsRunning(false)
      }
    }, 50)
  }, [])

  useEffect(() => {
    revalidate()
  }, [revalidate])

  return {
    isRunning,
    lastRun,
    runDuration,
    batchResult,
    shadowImpactReport,
    revalidate,
  }
}
